/* eslint-disable react/no-unescaped-entities */
import { FooterDataType } from "@/components/organisms/Footer";
import { COMPANY_LEGAL_NAME } from "@/shared/constants/common.constants";
import { footerData_en, footerData_us } from "@/shared/data/footer.data";
import { footerData_kr } from "@/shared/data/footer.data.kr";
import { NavbarDataType, navbarData_en, navbarData_us } from "@/shared/data/navbar.data";
import { navbarData_kr } from "@/shared/data/navbar.data.kr";
import { GetStaticProps, InferGetStaticPropsType } from "next";
import Head from "next/head";
import Link from "next/link";
import { PageTemplate } from "../../components/organisms/PageTemplate";

export const getStaticProps: GetStaticProps<{
  navbarData: NavbarDataType;
  footerData: FooterDataType;
}> = async (context) => {
  const locale = (context.locale as "en" | "kr" | "us" | "in") ?? "en";

  const navbarData = {
    en: navbarData_en,
    us: navbarData_us,
    kr: navbarData_kr,
    in: navbarData_en,
  }[locale];

  const footerData = {
    en: footerData_en,
    us: footerData_us,
    kr: footerData_kr,
    in: footerData_en,
  }[locale];

  return {
    props: {
      navbarData,
      footerData,
    },
  };
};
export default function LegalTermsPage({
  navbarData,
  footerData,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  return (
    <PageTemplate navbarData={navbarData} footerData={footerData}>
      <Head>
        <title>Terms of Service - Graphy</title>
        <meta name="description" content={"Graphy terms of service"} />
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      </Head>

      <div className="link-dash flex items-center justify-center px-6">
        <div className="WordSection1 max-w-3xl">
          <h1 className="mx-auto mt-24 text-center text-5xl font-extrabold text-black">
            Graphy Platform Terms of Service
          </h1>
          <h2 className="mb-10 pt-6 text-center text-xl uppercase tracking-ultrawide text-black">
            Last updated: 5th May 2022
          </h2>
          <div className="hidden md:block">
            <u className="mb-8 block text-center">
              <span className="text-extrabold text-center text-2xl tracking-tight">
                Table of Contents
              </span>
            </u>
            <div className="mb-4 flex flex-col">
              <span className={"text-xl"}>
                I. General Terms and Conditions{" "}
              </span>
            </div>
            <div className="flex flex-col items-start font-bold text-dash md:ml-4 md:flex-row">
              <div className="pr-3">
                <a className="text-base leading-7" href="#A">
                  A. About Graphy Platform
                </a>
                <br />
                <a className="text-base leading-7" href="#B">
                  B. Platform Services
                </a>
                <br />

                <a className="text-base leading-7" href="#C">
                  C. Right to Access and Registration
                </a>
                <br />

                <a className="text-base leading-7" href="#D">
                  D. Your Account
                </a>
                <br />

                <a className="text-base leading-7" href="#D">
                  E. Use of the Platform
                </a>
                <br />

                <a className="text-base leading-7" href="#F">
                  F. Your Content and Ownership
                </a>
                <br />

                <a className="text-base leading-7" href="#G">
                  G. Your Conduct on the Platform
                </a>
                <br />

                <a className="text-base leading-7" href="#H">
                  H. Use of Third-Party Services
                </a>
                <br />

                <a className="text-base leading-7" href="#I">
                  I. Compliance with Applicable Laws and Platform Terms
                </a>
                <br />

                <a className="text-base leading-7" href="#J">
                  J. Feedback
                </a>
                <br />

                <a className="text-base leading-7" href="#K">
                  K. Graphy's Rights
                </a>
                <br />

                <a className="text-base leading-7" href="#L">
                  L. Claims Against Use of Third-Party Intellectual Property
                </a>
                <br />

                <a className="text-base leading-7" href="#M">
                  M. Communications
                </a>
                <br />

                <a className="text-base leading-7" href="#N">
                  N. Processing of Personal Data by Creators
                </a>
                <br />

                <a className="text-base leading-7" href="#O">
                  O. Platform Availability
                </a>
                <br />

                <a className="text-base leading-7" href="#P">
                  P. Pricing, Payments, and Refunds
                </a>
                <br />

                <a className="text-base leading-7" href="#Q">
                  Q. Customized Subscription and Service Order
                </a>
                <br />

                <a className="text-base leading-7" href="#R">
                  R. Deletion of Account
                </a>
                <br />

                <a className="text-base leading-7" href="#S">
                  S. Disclaimer
                </a>
                <br />

                <a className="text-base leading-7" href="#T">
                  T. Limitation of Liability
                </a>
                <br />

                <a className="text-base leading-7" href="#U">
                  U. Indemnity and Release
                </a>
                <br />

                <a className="text-base leading-7" href="#V">
                  V. OFAC (Office of Foreign Asset Control)
                </a>
                <br />

                <a className="text-base leading-7" href="#W">
                  W. General Provisions
                </a>
                <br />
                <a className="text-base leading-7" href="#X">
                  X. Contact for User Support/Queries
                </a>
                <br />
              </div>
            </div>
            <div className="my-4 flex flex-col">
              <span className={"text-xl"}>
                II. Country Specific Terms and Conditions
              </span>
            </div>
            <div className="flex flex-col items-start font-bold text-dash md:ml-4 md:flex-row">
              <div className="pr-3">
                <a className="text-base leading-7" href="#A2">
                  A. Applicable in respect of Content published by Creators from
                  India.
                </a>
                <br />
                <a className="text-base leading-7" href="#B2">
                  B. Applicable in respect of Content published by Creators from
                  outside India.{" "}
                </a>
              </div>
            </div>
          </div>
          <div className="my-4 text-center">
            <span className="text-base font-bold">
              I. GENERAL TERMS AND CONDITIONS
            </span>
          </div>
          <p className="mb-3 text-justify text-base">
            <span className="text-base">
              These Terms of Service set out the terms and conditions for use of{" "}
            </span>
            <a href="https://graphy.com">https://graphy.com</a>
            <span className="text-base">
              {" "}
              (the <b>"Site"</b>), the mobile application(s) (the{" "}
              <b>"Application"</b>) and any features, subdomains, content
              (except as specified hereunder), functionality, services
              (including the Services), media, applications, or solutions
              offered on or through the Site and/or the Application and/or
              through any modes, medium, platform or format (hereinafter
              collectively referred to as the "<b>Platform</b>" or "
              <b>Graphy Platform</b>").{" "}
            </span>
          </p>
          <p className="mb-3 text-justify text-base">
            <span className="text-base">
              These Terms of Service apply to all users of the Platform
              including without limitation, (i) visitors of the Platform, (ii)
              all registered users of the Platform <b>(“Creator(s)”)</b> (iii)
              all users (other than Creator's 'End-Users') who are engaged by a
              Creator and/or are given access, limited or otherwise, to a
              Creator's Page (defined below) by such Creator, including such
              Creator's employees <b>(“Authorized Users”)</b> and (iv) all
              visitors and registered users of a Creator's Page{" "}
              <b>(“End-Users”)</b>; for ease of reference,{" "}
              <b>'Authorized Users'</b> and <b>'End-Users'</b> shall
              collectively be referred to as{" "}
              <b>“Creator's Page Users”/ “Page Users”</b>, where applicable.
            </span>
          </p>
          <p className="mb-3 text-justify text-base">
            Use of the terms <b>“you”, “your”,</b> or <b>“User(s)”</b> shall
            refer to the Creator(s) and all visitors of the Platform. Similarly,
            when we speak of <b>“Graphy”, “we”, “us”,</b> and <b>“our”</b>, we
            collectively mean <b>{COMPANY_LEGAL_NAME}</b> - a company
            incorporated in India, <b>Graphy INC</b> - a company incorporated in
            the State of Delaware, USA, and their respective affiliates. Kindly
            refer to the <b>“About Graphy Platform”</b> section of the Agreement
            to know which of the aforesaid Graphy entities will your Agreement
            be with.
          </p>
          <p className="mb-3 text-justify text-base">
            <span className="text-base">
              These Terms of Service along with the{" "}
              <Link href="/legal/privacy">Privacy Policy</Link>,{" "}
              <Link href="/dpa">Data Processing Addendum</Link>,{" "}
              <Link href="/legal/refund-policy">Refund Policy</Link>,{" "}
              <Link href="/cookies-policy">Cookies Policy</Link>,{" "}
              <Link href="/user-guidelines">the User Guidelines, </Link>
            </span>
            <span className="text-base">
              the <Link href="/takedown-policy">Takedown Policy</Link> and any
              other terms and conditions updated on the Platform or communicated
              to you from time to time (collectively referred to as the{" "}
              <b>“Platform Terms”</b>), defines the relationship and
              responsibilities between you and Graphy, and it governs your use
              of the Platform and the Services (defined below) offered therein.
              Your access to the Platform is subject to your acceptance of the
              Platform Terms, and such acceptance of the Platform Terms forms a
              legally binding agreement between you and Graphy{" "}
              <b>(“Agreement”)</b>. Hence, please take your time to read the
              Platform Terms in their entirety.
            </span>
          </p>
          <p className="mb-3 text-justify text-base">
            From time-to-time, updated versions of the Platform Terms may be
            made available on the Platform for your reference. By visiting the
            Platform, providing your Personal Data{" "}
            <i>(as defined in the Privacy Policy)</i>, using the Services
            offered or by otherwise signalling your agreement when the option is
            presented to you, you hereby expressly accept and agree to the
            Platform Terms. If you do not agree to any of the terms or do not
            wish to be bound by them, then please do not use the Platform in any
            manner.
          </p>
          <p className="mb-3 text-justify text-base">
            All capitalized terms are defined/have the meaning assigned to it
            under these Terms of Service and/or other Platform Terms. In other
            words, if you find any word herein that has its first letter
            capitalized, then it means that we have explained the meaning such
            word has in the context of these Terms of Service and/or other
            Platform Terms; if not here, then it will be provided in the
            relevant Platform Terms - when we refer to any such term in these
            Terms of Service, we will try and specify where in these Terms of
            Service and/or other Platform Terms you can find the
            meaning/definition.
          </p>
          <p className="mb-3 text-justify text-base">
            <span className="text-base font-bold" id="A">
              A. About Graphy Platform:{" "}
            </span>
          </p>
          <p className="mb-3 text-justify text-base">
            Graphy Platform is owned by {COMPANY_LEGAL_NAME}, a company
            incorporated under the (Indian) Companies Act, 2013, having its
            registered office at Maruti Infotech Centre, 2nd Floor, B-Block,
            Domlur, Koramangala Inner Ring Road, Bangalore- 560 071, Karnataka,
            India. The Platform is managed and operated by Graphy India for
            Indian territory, and managed and operated by Graphy INC, a US
            corporation, having its registered office at Vistra (Delaware) Ltd,
            3500, South Dupont, HWY, Dover, Kent, DE 19901, Delaware, USA
            <b>(“Graphy Inc”)</b> in respect of territories outside India.
          </p>
          <p className="mb-3 text-justify text-base">
            Accordingly, (i) if you are visiting / registering on the Platform
            from India, then, your Agreement shall be with Graphy India, and
            (ii) if you are visiting / registering on the Platform from any
            country outside India, then, your Agreement shall be with Graphy
            Inc.
          </p>
          <p className="mb-3 text-justify text-base">
            Further, please refer to the{" "}
            <b>“Country Specific Terms and Conditions”</b> section of these
            Terms of Service to know the specific terms, if any, applicable to
            you depending on the above or your place of residence.
          </p>

          <p className="mb-3 text-justify text-base">
            <span className="text-base font-bold" id="B">
              B. Platform Services:{" "}
            </span>
          </p>
          <p className="mb-3 text-justify text-base">
            Graphy Platform is an online service that provides a platform for
            Creators to create, design, upload, publish and sell content,
            including without limitation audio-visuals, audio, and electronic
            versions of written content directly to their End Users by enabling
            each Creator to create their own Page (defined below) in the form of
            sub-domains (hereinafter referred to as <b>“Content”</b>).
          </p>
          <p className="mb-3 text-justify text-base">
            As a Creator, the Platform enables you to create Content using
            several features available on the Platform. Additionally, the
            Platform also provides various features to help Creators better
            communicate and interact with their End-Users, including an option
            to enable discussion forums between Creators and their End-Users.
            These features are available on each Creator's Page and Creators
            shall have the ability to enable or disable any such features for
            access by their respective End-Users.
          </p>
          <p className="mb-3 text-justify text-base">
            For the purpose of the Platform Terms, usage of the term{" "}
            <b>“Services”</b> shall mean and include all services,
            functionalities and tools offered via the Platform by Graphy.
          </p>
          <p className="mb-3 text-justify text-base">
            Graphy is only a platform service provider and an 'intermediary' in
            accordance with the Information Technology Act, 2000 including the
            Information Technology (Intermediary Guidelines and Digital Media
            Ethics Code) Rules, 2021 and the Digital Millennium Copyright Act
            (as amended from time to time) or other equivalent / similar
            legislations; and Graphy is not, in any manner, an educational
            institution, a content provider and/or a marketplace. Our Platform
            is merely a technology that facilitates creation and sale of Content
            by Creators and enables interaction between Creators and their
            End-Users in association with such Content. Graphy's only
            responsibility is to provision the technology and the Services in
            accordance with the Platform Terms. All Content on a Creator's Page,
            the pricing for registration <b>(“Creator's Fee”)</b> or access to
            such Content by a Creator's Page Users and the overall management of
            a Creator's Page (excluding the technological reliance on Graphy),
            including any interactions between a Creator and its End-Users, and
            the services offered therein is solely decided by the Creator, and
            accordingly, the Creator is, at all times, solely responsible for
            all of the aforesaid. Graphy is not, in any manner, liable for any
            disputes, claims, losses, injuries, or damage of any kind that may
            arise out of a Creator's relationship with such Creator's Page
            Users, including Creator's End-Users' reliance upon any information
            or other content made available on Creator's Page.
          </p>
          <p className="mb-3 text-justify text-base">
            The Services rendered by Graphy are not exclusive to you, and we
            reserve the right to provide our Services to any person, natural or
            legal, including your competitors. You further acknowledge and agree
            that our employees and contractors may also be our customers and
            Creators on the Platform and that they may compete with you,
            however, they shall be bound by the confidentiality obligations
            under their respective contracts with us and shall not use your
            confidential information as a User of the Platform, in the event any
            such confidential information is made available to them for the
            purposes of rendering the Services in accordance with the Platform
            Terms.{" "}
          </p>
          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base" id="C">
                C. Right to Access and Registration:
              </span>
            </b>
          </p>
          <p className="mb-3 text-justify text-base">
            To access the Platform and use the Services, you will be required to
            register and create an account on the Platform by providing the
            requested details. You can either register as an individual or for
            an entity. If you are registering for an entity, then you confirm
            that you have all the required permission and authority to register
            and represent such entity on the Platform. If you are an individual,
            then you hereby confirm that you meet the 'Age Requirements'
            specified hereinbelow. By continued usage of the Platform, you,
            through your actions, represent and warrant that you continue to
            comply with the Age Requirements.
          </p>
          <p className="mb-3 text-justify text-base">
            You can register on the Platform for free, however, the Services
            offered on the Platform may be chargeable. Please review our{" "}
            <Link href="/pricing">Pricing Page</Link> to know the Service
            offerings. You are not obligated to purchase any product or use any
            Service offered on the Platform.
          </p>

          <p className="mb-3 text-justify text-base font-semibold">
            Age Requirements to register and use on the Platform (“Age
            Requirements”):
          </p>
          <div className="mb-3 text-justify text-base">
            <ol className="ml-6 list-outside list-decimal">
              <li className="pl-2 md:pl-4">
                If you are a resident of India or any other country (except the
                USA, UK or EU countries), then you must have attained at least
                18 (eighteen) years of age or such age of majority in accordance
                with the applicable laws of your country of residence to
                register and use the Platform and the Services offered there;
              </li>
              <li className="pl-2 md:pl-4">
                If you are a resident of the European Union (EU) country and
                have attained at least 16 (sixteen) years of age, then you are
                permitted to register and use the Platform; and
              </li>
              <li className="pl-2 md:pl-4">
                If you are a resident of the United States of America (USA) or
                United Kingdom (UK) and have attained at least 13 (thirteen)
                years of age, then you can register and use the Platform.
              </li>
              <li className="pl-2 md:pl-4">
                <p className="mb-3 text-justify text-base">
                  If you are a resident of the US, UK or EU countries and are
                  between the ages 13 and 18/ 16 and 18 respectively or if you
                  are resident of any other country and are considered to be of
                  any age determined for use of internet services but are less
                  than the contractual age determined by the applicable laws of
                  such country, then by using the Platform, you confirm to us
                  that your use of the Platform is with the permission of your
                  parent or legal guardian <b>(“Parent”)</b>, and your Parent
                  has read, agreed and accepted to the Agreement; and in the
                  event of any dispute between you and Graphy pursuant to your
                  Agreement with us, the terms of our Agreement shall be
                  applicable to and enforceable against your Parent.
                </p>
                <p className="mb-3 text-justify text-base">
                  Please note that the Platform and the Services offered therein
                  are not directed at Minors/Children and Minors/Children are
                  not by themselves eligible to register on and use the Platform
                  and/or the Service offered therein. For the purpose of these
                  Platform Terms, <b>“Minor(s)”/ “Child”/ “Children”</b> is/are
                  individual(s) who do(es) not fulfill the Age Requirements
                  mentioned above (as may be revised as per applicable laws from
                  time to time).
                </p>
              </li>
            </ol>
          </div>

          <p className="mb-3 text-justify text-base">
            Graphy reserves the right to terminate your Subscription (as defined
            under the{" "}
            <b>
              <i>'Pricing, Payments and Refunds'</i>
            </b>{" "}
            section below) and / or restrict your access to the Platform, if it
            is discovered that you do not meet the Age Requirements. You
            acknowledge that Graphy does not have the responsibility to ensure
            that you conform to the aforesaid Age Requirements. It shall be your
            sole responsibility to ensure that the required qualifications are
            met, and continue to be met with, at the time of your registration
            on and/or use of the Platform.
          </p>
          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base" id="D">
                D. Your Account:
              </span>
            </b>
          </p>
          <p className="mb-3 text-justify text-base">
            On completion of your registration on the Platform as a Creator, a
            subdomain gets created based on the information provided by you on
            the Platform <b>(“Creator's Page” or “Page”)</b>; and based on the
            service offerings purchased/subscribed by you on the Platform, you
            may also get access to an android and/or iOS mobile application that
            is specific to your Page or that is commonly made available to other
            Creators subscribing to similar services as you{" "}
            <b>(“Creator's Application”)</b>. Accordingly, any reference to{" "}
            <b>'Creator's Page'</b> or
            <b>'Page'</b> shall mean and include the Creator's Application, as
            applicable, and all features made available to a Creator pursuant to
            the Subscription Plan purchased by the Creator.
          </p>
          <p className="mb-3 text-justify text-base">
            As a Creator, you or the entity on whose behalf you have created an
            account on the Platform (as may be the case), are/is the deemed
            account holder of the account so created by you. You have the option
            to provide limited or full access to your account to any of your
            Authorized Users who may be assigned various roles including the
            roles of admins, instructors, affiliates who promote your Page or
            Content, or subscribers. Please note that you shall be solely
            responsible for any and all acts or omissions by your Page Users.
            Accordingly, the Platform Terms and any other specific terms you may
            have agreed to in writing (emails included) with Graphy in
            association with your use of the Platform, shall be deemed to be
            applicable to all your Page Users.
          </p>
          <p className="mb-3 text-justify text-base">
            You are responsible for maintaining the confidentiality of your
            account and password to access the Platform. You acknowledge that
            your account is personal to you and agree not to provide any other
            person (except the Authorized Users) with access to the Platform and
            to restrict access to your device to prevent any unauthorized access
            to your account. You agree to accept responsibility for all
            activities that occur under your account. You should use particular
            caution when accessing your account from a public or shared computer
            so that others are not able to view or record your username,
            password, or other personal information. You should take all
            necessary steps to ensure that the password is kept confidential and
            secure at all times, and if you have any reason to believe that your
            password has become known to anyone else, or if the password is
            being, or is likely to be, used in an unauthorized manner, you agree
            to immediately change your password or inform us of any unauthorized
            access to or use of your username or password, so that we are able
            to assist you in stopping or preventing such unauthorized access.
            Please ensure that the details you provide us are correct in
            accordance with the details you may have submitted on the Platform.
          </p>
          <p className="mb-3 text-justify text-base">
            You represent that the information provided by you at the time of
            the registration are correct, true and accurate and you are
            authorized to provide such information, and you agree to update the
            same as and when there is any change in the said information. Please
            read our <Link href="privacy">Privacy Policy</Link> to understand
            how we handle your information.
          </p>
          <p className="mb-3 text-justify text-base">
            Please note that we reserve the right to reject or put on-hold, your
            registration on the Platform as may be required to comply with any
            legal and regulatory provisions, and we also reserve the right to
            refuse access to the Platform, terminate accounts, remove content at
            any time, and/or reserve the right to disable any account, feature,
            or identifiers, whether chosen by you or provided by us, at any time
            if, in our opinion, any information provided by you violates or you
            have violated, any provision of the Platform Terms or any applicable
            law or any government policies.
          </p>
          <p className="mb-3 text-justify text-base">
            In the event of a dispute regarding your account ownership, we
            reserve the right to request documentation to determine or confirm
            account ownership. Documentation may include without limitation, a
            scanned copy of your organizational documents, government issued
            photo ID, and any applicable business licences. After due
            verification, we retain the right to determine the rightful account
            owner and transfer such disputed account to the rightful owner. If
            we are unable to reasonably determine the rightful account owner, we
            reserve the right to temporarily disable an account until resolution
            has been determined between the disputing parties.
          </p>
          <p className="mb-3 text-justify text-base">
            Further, to access the Platform, create, publish and/or view the
            Content on the Platform, you will need to use a
            <b> “Supported/Compatible Device”</b> which means a personal
            computer, mobile phone, portable media player, or other electronic
            device that meets the system and compatibility requirements and on
            which you are authorized to use the Platform. The
            Supported/Compatible Devices to access the Platform may change from
            time to time and, in some cases, whether a device is (or remains) a
            Supported/Compatible Device may depend on software or systems
            provided or maintained by the device manufacturer or other third
            parties. Accordingly, devices that are Supported/Compatible Devices
            at one time may cease to be Supported/Compatible Devices in the
            future. Thus, kindly make sure that the device that you use is
            compatible with our system/software to use the Platform and avail
            the Services offered therein. Further, similar Supported /
            Compatible Device requirements would apply to your End-Users and the
            responsibility of communicating these requirements to the End-Users
            shall be solely yours.
          </p>
          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base" id="F">
                E. Use of the Platform:
              </span>
            </b>
          </p>
          <div className="mb-3 text-justify text-base">
            <ol className="ml-6 list-outside list-decimal">
              <li className="pl-2 md:pl-4">
                <p className="mb-3 text-justify text-base">
                  <b>Graphy's Intellectual Property:</b> Graphy owns all
                  information and materials (in whatever form or media) provided
                  or communicated or licensed to you by or on behalf of Graphy
                  including but not limited to, the Platform, the trademarks,
                  illustrations, letters, images, ideas, concepts, the layout,
                  design, flow, look and feel of the Platform, logos, marks,
                  graphics, audio files, video files, any software which is
                  owned by or licensed to Graphy, the underlying source and
                  object code, instructions embedded in any form of digital
                  documents and other data, information, or material made
                  available to you by Graphy
                  <b>(“Graphy's Intellectual Property” or “Graphy's IP”)</b>.
                  Graphy's IP specifically excludes any Content on Creator's
                  Page (defined under the <b>'Your Content and Ownership'</b>{" "}
                  section below) and any content uploaded by other Users on the
                  Platform, including any brand names and trademarks that are
                  not owned by Graphy. Graphy's IP will not be used, modified,
                  or altered by you in any way. You acknowledge and agree that
                  you do not acquire any ownership or rights to the Graphy's IP
                  by use of the Platform. You acknowledge and agree that the
                  Graphy's IP is protected by the copyright, trademark, patent,
                  trade secret and other intellectual property or proprietary
                  rights laws and any unauthorize use, reproduction,
                  modification, distribution, transmission, republication,
                  display, or performance of the Graphy's IP and/or any
                  component thereof is strictly prohibited and may result in
                  legal / penal consequences.
                </p>
                <p className="mb-3 text-justify text-base">
                  It is further clarified that, Graphy, its logo, and other
                  marks indicated on our Platform are our trademarks or
                  registered trademarks. Platform's graphics, logos, page
                  headers, button icons, scripts and service names are the
                  trademarks or trade dress of the Platform. Graphy's trademarks
                  and trade dress may not be used in connection with any product
                  or service that is not Graphy's, in any manner that is likely
                  to cause confusion among customers, or in any manner that
                  disparages or discredits Graphy. All other trademarks not
                  owned by Graphy that appear on this Platform are the property
                  of their respective owners, who may or may not be gated with,
                  or connected to Graphy.
                </p>
              </li>
              <li className="pl-2 md:pl-4">
                <p className="mb-3 text-justify text-base">
                  <b>Licence to use the Platform:</b> As a Creator, Graphy
                  grants you and your Authorized Users a limited, non-exclusive,
                  non-transferable, non-sublicensable licence to access and use
                  the Platform for your own personal and commercial use in
                  accordance with the Platform Terms.
                </p>
                <p className="mb-3 text-justify text-base">
                  Further, to facilitate your services to your End-Users through
                  the Platform, Graphy grants your End-Users, a limited,
                  non-exclusive licence to access and use the Platform through
                  your Page for their own personal use. This licence does not
                  grant your End-Users the right to assign or sublicense the
                  licence granted hereunder to anyone else.
                </p>
                <p className="mb-3 text-justify text-base">
                  Except as expressly permitted under the Platform Terms or
                  otherwise in writing, you will not, either by yourself or
                  through any other person, reproduce, duplicate, copy, sell,
                  redistribute, create derivative works or otherwise exploit the
                  Platform or any portion of the Platform (including but not
                  limited to any copyrighted material, trademarks, or other
                  proprietary information).
                </p>
              </li>
              <li className="pl-2 md:pl-4">
                <div className="mb-3 text-justify text-base">
                  <b>Restrictions on usage of the Platform:</b> The licence
                  granted to you and your Page Users by Graphy, is limited to
                  the purpose defined under the Platform Terms. Neither you nor
                  your Page Users shall use the Platform for any purpose or in
                  any manner other than as expressly permitted under the
                  Platform Terms, and you shall not, either by yourselves or
                  through any other person, do or cause the doing of, any act or
                  omission that would result in:
                </div>
                <div className="mb-3 text-justify text-base">
                  <ol
                    type="a"
                    className="ml-4 list-outside"
                    style={{ listStyle: "lower-alpha" }}
                  >
                    <li className="pl-1 md:pl-2">
                      distribution of any part of the Platform or the Graphy's
                      IP (as defined above), through any mode or medium, without
                      Graphy's prior written authorization.
                    </li>
                    <li className="pl-1 md:pl-2">
                      alteration or modification of or tampering with any part
                      of the Platform or result in creation of other derivative
                      works of the Platform or the Services offered therein.
                    </li>
                    <li className="pl-1 md:pl-2">
                      reverse engineering, disassembly, or de-compilation of the
                      Platform or any part thereof or result in derivation of
                      the source code of any software included in the Platform
                      or made available to you as part of the Services using
                      means or processes that are deemed unauthorized and/or
                      illegal.
                    </li>
                    <li className="pl-1 md:pl-2">
                      accessing or using the Platform in a way intended to avoid
                      incurring fees or exceeding usage limits or quotas.
                    </li>
                    <li className="pl-1 md:pl-2">
                      gaining access to another User's content to which you do
                      not have authorized or legitimate access.
                    </li>
                    <li className="pl-1 md:pl-2">
                      circumvention or attempt to circumvent any limitations
                      imposed on your account;
                    </li>
                    <li className="pl-1 md:pl-2">
                      probing, scanning, or testing of the vulnerability of any
                      system or network used by Graphy, unless Graphy has given
                      you a prior written authorization.
                    </li>
                    <li className="pl-1 md:pl-2">
                      the use of Services to collect or store any Sensitive
                      Personal Data <i>(as defined in our Privacy Policy)</i>{" "}
                      including personal health information, banking and credit
                      card information; and if you do use the Platform and/or
                      the Services to collect, store or otherwise process any
                      Sensitive Personal Data, then you do so at your own risk
                      and you are responsible for ensuring compliance with all
                      applicable laws;
                    </li>
                    <li className="pl-1 md:pl-2">
                      sale of access to the Platform (other than as permitted
                      under the Platform Terms) or sale of any advertising,
                      sponsorship or promotion space on or within the Platform,
                      without Graphy's prior written approval.
                    </li>
                    <li className="pl-1 md:pl-2">
                      use or launch of any automated system, including without
                      limitation, “robots,” “spiders,” or “offline readers,”
                      that accesses the Platform in a manner that sends more
                      request messages to Graphy's servers in a given period of
                      time than a human can reasonably produce in the same
                      period by using a conventional on-line web browser.
                    </li>
                    <li className="pl-1 md:pl-2">
                      unauthorized collection or harvesting of any personally
                      identifiable information, including account names, from
                      the Platform, or in unauthorized use of the communication
                      systems provided by the Platform.
                    </li>
                    <li className="pl-1 md:pl-2">
                      extraction of any substantial parts of this Platform for
                      re-utilization, including through utilization of any data
                      mining tools, robots, or similar data gathering and
                      extraction tools, without Graphy's express prior written
                      consent.
                    </li>
                    <li className="pl-1 md:pl-2">
                      purchase of search engine or other pay per click keywords
                      (such as Google AdWords), or domain names that use the
                      name 'Graphy' or Graphy's trademarks and/or variations and
                      misspellings thereof.
                    </li>
                  </ol>
                </div>
              </li>
              <li className="pl-2 md:pl-4">
                <div className="mb-3 text-justify text-base">
                  <b>Other terms applicable for your use of the Platform:</b>
                </div>
                <div className="mb-3 text-justify text-base">
                  <ol
                    type="a"
                    className="ml-4 list-outside"
                    style={{ listStyle: "lower-alpha" }}
                  >
                    <li className="pl-1 md:pl-2">
                      You agree to receive installs and updates from time to
                      time from Graphy. These updates are designed to improve,
                      enhance, and further develop the Platform and may take the
                      form of bug fixes, enhanced functions, new software
                      modules and completely new versions. You agree to receive
                      such updates (and permit Graphy to deliver these to you)
                      as part of your use of the Platform.
                    </li>
                    <li className="pl-1 md:pl-2">
                      Notwithstanding anything stated to the contrary herein in
                      this section of the Platform Terms, Graphy grants the
                      operators of public search engines permission to use
                      spiders to copy materials from the Platform for the sole
                      purpose of and solely to the extent necessary for creating
                      publicly available searchable indices of the materials,
                      but not caches or archives of such materials. Graphy
                      reserves the right to revoke these exceptions either
                      generally or in specific cases at any time with or without
                      providing any notice in this regard.
                    </li>
                    <li className="pl-1 md:pl-2">
                      You may post reviews, comments and other Content; send
                      other communications; and submit suggestions, ideas,
                      comments, questions or other information as long as the
                      Content so submitted or sent is not illegal, obscene,
                      threatening, defamatory, invasive of privacy, infringement
                      of intellectual property rights, or otherwise injurious to
                      third parties or objectionable, and does not consist of or
                      contain software viruses, political campaigning,
                      commercial solicitation, chain letters, mass mailings or
                      any other form of spam.
                    </li>
                    <li className="pl-1 md:pl-2">
                      You will not misrepresent or embellish the relationship
                      between Graphy and you (including by expressing or
                      implying that we support, sponsor, endorse, or contribute
                      to you or your business endeavours). You will not imply
                      any relationship or affiliation between Graphy and you.
                    </li>
                    <li className="pl-1 md:pl-2">
                      You are solely responsible for all customer service issues
                      relating to your services, including pricing, fulfilment,
                      cancellation by you or your End-User, returns, refunds and
                      adjustments, rebates, functionality and warranty,
                      technical support, and feedback, reviews, or ratings
                      concerning experiences with your Authorized Users,
                      policies, or processes. In performing customer service,
                      you will always present yourself as a separate entity from
                      Graphy. As between you and Graphy, Graphy shall support
                      you only to the extent of Services offered by Graphy
                      relating to your account in accordance with the Agreement.
                    </li>
                  </ol>
                </div>
              </li>
            </ol>
          </div>
          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base" id="F">
                F. Your Content and Ownership:
              </span>
            </b>
          </p>
          <div className="mb-3 text-justify text-base">
            <ol className="ml-6 list-outside list-decimal">
              <li className="pl-2 md:pl-4">
                <p className="mb-3 text-justify text-base">
                  <b>Ownership of your Content: </b> You shall remain the sole
                  owner of any content uploaded, published, submitted, or posted
                  by you on the Platform, including without limitation, the
                  Content, any text, image, media, written statements, or other
                  content posted or published by you on the Platform{" "}
                  <b>(“User Content”)</b>, and Graphy does not claim any
                  ownership over any User Content uploaded by you on the
                  Platform.
                </p>
                <p className="mb-3 text-justify text-base">
                  Further, you shall remain solely responsible for any content
                  uploaded, published, submitted, or posted by your Page Users
                  on your Page, including without limitation, any text, image,
                  media, written statements, or other content posted or
                  published on your Page <b>(“Content on Creator's Page”)</b>.
                  Neither does Graphy claim any ownership or rights over such
                  Content on Creator's Page, nor shall Graphy be responsible for
                  any such Content on Creator's Page.
                </p>
                <p className="mb-3 text-justify text-base">
                  For ease of reference, <b>“Content on Creators Page”</b> shall
                  include User Content, unless the terms are used individually
                  and specifically where required.
                </p>
              </li>
              <li className="pl-2 md:pl-4">
                <div className="mb-3 text-justify text-base">
                  <b>Terms applicable for the Content on Creator's Page:</b>
                </div>
                <div className="mb-3 text-justify text-base">
                  <ol
                    type="a"
                    className="ml-4 list-outside"
                    style={{ listStyle: "lower-alpha" }}
                  >
                    <li className="pl-1 md:pl-2">
                      You shall be solely responsible for any Content on
                      Creator's Page and the consequences of submitting and
                      publishing such content on the Platform. In respect of
                      your User Content, you affirm, represent, and warrant that
                      you own or have the necessary licences, rights, consents,
                      and permissions to publish the User Content, and in
                      respect of Content on Creator's Page (excluding the User
                      Content), you hereby represent and warrant that you have
                      the rights, consents and permissions from your Page Users
                      as may be required to grant the necessary rights to Graphy
                      to render the Services in accordance with the Platform
                      Terms.
                    </li>
                    <li className="pl-1 md:pl-2">
                      You further agree that no Content on Creator's Page will
                      contain any third party copyrighted material, or material
                      that is subject to other third-party proprietary rights,
                      unless you have permission from the rightful owner of the
                      material, or you are otherwise legally entitled to post
                      the material and to grant Graphy all the licenced rights
                      granted herein in association with such content.
                    </li>
                    <li className="pl-1 md:pl-2">
                      You confirm that you shall not host, display, upload,
                      modify, publish, transmit, store, update or share any such
                      Content on Creator's Page or any information on the
                      Platform that:
                      <div className="mb-3 text-justify text-base">
                        <ol
                          type="a"
                          className="ml-4 list-outside"
                          style={{ listStyle: "lower-roman" }}
                        >
                          <li className="pl-1 md:pl-2">
                            is defamatory, obscene, pornographic, invasive of
                            another's privacy including bodily privacy,
                            insulting or harassing on the basis of gender,
                            libelous, racially or ethnically objectionable,
                            relating or encouraging money laundering or
                            gambling, or otherwise inconsistent with or contrary
                            to the laws in force;
                          </li>
                          <li className="pl-1 md:pl-2">
                            is harmful to Children;
                          </li>
                          <li className="pl-1 md:pl-2">
                            infringes any patent, trademark, copyright or other
                            proprietary rights of another;
                          </li>
                          <li className="pl-1 md:pl-2">
                            violates any law, statute, ordinance or regulation
                            or Platform Terms;
                          </li>
                          <li className="pl-1 md:pl-2">
                            deceives or misleads any User, including your
                            End-Users, about the origin of the message or
                            knowingly and intentionally communicates any
                            information which is patently false or misleading in
                            nature but may reasonably be perceived as a fact;
                          </li>
                          <li className="pl-1 md:pl-2">
                            impersonates another person;
                          </li>
                          <li className="pl-1 md:pl-2">
                            threatens the unity, integrity, defense, security or
                            sovereignty of India, friendly relations with
                            foreign States, or public order, or causes
                            incitement to the commission of any cognizable
                            offence or prevents investigation of any offence or
                            is insulting other nation;
                          </li>
                          <li className="pl-1 md:pl-2">
                            contains software virus or any other computer code,
                            file or program designed to interrupt, destroy or
                            limit the functionality of any computer resource;
                          </li>
                          <li className="pl-1 md:pl-2">
                            is patently false and untrue, and is written or
                            published in any form, with the intent to mislead or
                            harass a person, entity or agency for financial gain
                            or to cause any injury to any person;
                          </li>
                          <li className="pl-1 md:pl-2">
                            is false, inaccurate or misleading;
                          </li>
                          <li className="pl-1 md:pl-2">
                            is obscene or contain pornography.
                          </li>
                          <li className="pl-1 md:pl-2">
                            contains any viruses, trojan horses, worms,
                            cancelbots or other computer programming routines
                            that may damage, detrimentally interfere with,
                            surreptitiously intercept, or expropriate any
                            system, data or personal information.
                          </li>
                        </ol>
                      </div>
                    </li>
                    <li className="pl-1 md:pl-2">
                      You understand that Graphy does not guarantee any
                      confidentiality with respect to any Content on Creator's
                      Page.
                    </li>
                  </ol>
                </div>
              </li>
              <li className="pl-2 md:pl-4">
                <b>Licence to Content on Creator's Page:</b> By hosting,
                displaying, uploading, and/or publishing the Content on
                Creator's Page and by allowing transmission and storage thereof
                on the Platform, you hereby grant Graphy a limited, worldwide,
                non-exclusive, royalty-free, and sub-licensable licence to (i)
                reproduce, distribute, display, publish, make available online
                or otherwise electronically transmit such Content on Creator's
                Page through the Platform, (ii) to store and / or retain such
                Content on Creator's Page in order to facilitate the Services,
                and (iii) to comply with Graphy's obligations under the Platform
                Terms and applicable laws. For more details, please refer our
                Privacy Policy.
              </li>
            </ol>
          </div>

          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base" id="G">
                G. Your Conduct on the Platform:
              </span>
            </b>
          </p>
          <p className="mb-3 text-justify text-base">
            Your conduct on the Platform shall strictly be in accordance with
            the <Link href="/user-guidelines">User Guidelines</Link> published
            on the Platform.
          </p>
          <p className="mb-3 text-justify text-base">
            Further, you understand and confirm that you shall not during your
            use of the Platform at any time host, display, modify, post, publish
            or allow transmission of any content, comments, or act on or through
            your Page in any way which will amount to harassment of any other
            User or Page User. If at any given point it comes to Graphy's notice
            that you have engaged in any kind of harassment of other Users or
            Page Users, then in such a case you agree that Graphy shall have the
            sole right to suspend/terminate your account with immediate effect
            and without any notice of such suspension or termination; and we
            also reserve the right in our sole discretion to initiate any legal
            proceedings against you in such cases.
          </p>

          <p className="mb-3 text-justify text-base">
            We are not obligated to monitor any content published on the
            Platform or review the conduct occurring on or through the Platform,
            including the interactions between the Creators and their End-Users.
            However, if any violation of the User Guidelines and/or of any
            applicable laws is brought to our notice, then, as an 'intermediary'
            we reserve the right to take necessary actions in order to comply
            with our obligation under the Information Technology Act, 2000, or
            the Digital Millennium Copyright Act (as amended from time to time)
            or other equivalent / similar legislations; such actions, without
            limitation, may include, refusal to allow access to your account on
            the Platform, suspension of Services or your account, terminate your
            accounts or remove such violating content at any time.
          </p>

          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base" id="H">
                H. Use of Third-Party Services:
              </span>
            </b>
          </p>
          <p className="mb-3 text-justify text-base">
            <span className="text-base">
              To enhance your experience of the Platform and help you best
              utilize the Services, we may recommend integration of or provide
              you the access to third-party software, applications, or services
              <b>(“Third-Party Platforms”)</b>; these recommendations or
              provision to use services of such Third-Party Platforms are only
              optional tools or integrations necessarily made available for your
              convenience, and you are not obligated to use any service of such
              Third-Party Platforms. However, if you choose to use such
              services, then your use of the Third-Party Platform(s) services or
              the services of any third-party service provider is solely subject
              to the terms that may be agreed between you and the Third-Party
              Platform(s) (including any other third-party service providers)
              and shall be at your own risk. We strongly suggest that you read
              the terms and conditions, privacy policies and/or any other
              policies that may be made available to you by the Third-Party
              Platform(s) (including any other third-party service providers)
              either on their platforms or otherwise, prior to subscribing or
              using any of their services; and you may make inquiries as you
              feel necessary or appropriate before proceeding with any
              transaction with any such Third-Party Platform (including any
              other third-party service providers).
            </span>
          </p>
          <p className="mb-3 text-justify text-base">
            If you use the services of any Third-Party Platform (including any
            other third-party service providers) in association with the
            Services you have subscribed to on the Platform, then (i) you
            represent to us that you have agreed to be bound by such Third Party
            Platform’s (including any other third-party service providers) terms
            and conditions and (ii) you grant us the permission to allow such
            Third-Party Platform (including any other third-party service
            providers) to access the Content on Creator’s Page and/or such other
            data associated with you to the extent required to integrate and
            facilitate the services of such Third-Party Platform(s) (including
            any other third-party service providers) in association with the
            Services you have subscribed to on the Platform. If you do not agree
            to any of the terms and conditions of such Third-Party Platform(s)
            (including any other third-party service providers), then you should
            not install or use their services with our Services.
          </p>
          <p className="mb-3 text-justify text-base">
            Similarly, we may also provide you access to an online directory of
            independent third parties <b>(“Experts”)</b> who can help you build
            and operate your Page. However, we do not employ these Experts and
            are in no way affiliated with these Experts. We only provide you
            with an index of Experts from which you may or may not choose to use
            the services of an Expert. We do not endorse any Expert. The
            information provided on the Platform regarding an Expert and
            responses to email inquiries, if any on such Experts, are for
            information purposes only, and the same shall not be construed or
            otherwise implied as an affiliation, recommendation or endorsement
            of any Expert or their services, websites or businesses.
          </p>
          <p className="mb-3 text-justify text-base">
            Third-Party Platform (including any other third-party service
            providers) and Experts shall hereinafter collectively be referred to
            as <b>“Third Party Services”</b>, where applicable.
          </p>
          <p className="mb-3 text-justify text-base">
            Neither do we have any control over, nor do we monitor the services
            of any such Third-Party Services. We make no claim, warranty, or
            representation regarding any Third-Party Services, their services,
            policies, or reliability thereof. You hereby expressly release
            Graphy from any and all liabilities arising from or in association
            with your use of such Third-Party Services.
          </p>
          <p className="mb-3 text-justify text-base">
            Further, Graphy does not guarantee the availability of the
            Third-Party Services, and you acknowledge and agree that Graphy may
            disable access to any such Third-Party Services in its sole
            discretion.
          </p>
          <p className="mb-3 text-justify text-base">
            UNDER NO CIRCUMSTANCES SHALL GRAPHY BE LIABLE FOR ANY DIRECT,
            INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY OR OTHER
            DAMAGES WHATSOEVER, INCLUDING, WITHOUT LIMITATION, ANY SUCH DAMAGES
            THAT RESULT FROM ANY CONTRACTUAL RELATIONSHIP BETWEEN YOU AND ANY
            THIRD-PARTY SERVICES. THESE LIMITATIONS SHALL APPLY EVEN IF GRAPHY
            HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. THE FOREGOING
            LIMITATIONS SHALL APPLY TO THE FULLEST EXTENT PERMITTED BY LAW.
          </p>
          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base" id="I">
                I. Compliance with Applicable Laws and the Platform Terms:
              </span>
            </b>
          </p>
          <p className="mb-3 text-justify text-base">
            You understand and agree that you are solely and exclusively
            responsible for your Page, determination of Creator’s Fee, access to
            Content and the overall management of your Page (excluding the
            technological reliance on Graphy) and the services offered therein
            in accordance with the applicable laws and the Platform Terms. You
            are solely responsible for publishing any terms and conditions,
            privacy policies and any other terms and conditions or policies that
            shall be applicable for your Page Users. All terms and policies made
            available by you must be in compliance of the applicable laws,
            including Data Protection Laws{" "}
            <i>
              (as defined in the <Link href="privacy">Privacy Policy</Link>)
            </i>{" "}
            and the Platform Terms.
          </p>
          <p className="mb-3 text-justify text-base">
            Graphy may make available informational materials through articles,
            templates and/or frequently asked questions (FAQs) on the Platform
            for your reference. However, such materials are informational in
            nature only, and must not be considered as legal advice or
            documentation. Graphy provides no warranty, express or implied, in
            respect of such informational materials, including as to their
            completeness or legal impact. We suggest that you seek professional
            legal advice prior to publishing any terms or policies on your Page
            at your own expense to ensure your compliance with all applicable
            laws and the Platform Terms.
          </p>
          <p className="mb-3 text-justify text-base">
            Furthermore, as stated in the{" "}
            <b>
              <u>'Right to Access and Registration'</u>
            </b>
            section above, our Platform is not directed at Minors/Children and
            urge you to follow and apply the same in respect of your End-Users.
            Similarly, as stated in the{" "}
            <b>'Restriction on usage of the Platform'</b>
            section above, Graphy does not collect or facilitate collection of
            any Sensitive Personal Data, including credit card or bank details
            and any health information; accordingly, you may not use the
            Platform or the Services to collect, store, or process any such
            Sensitive Personal Data. However, in your use of the Services, (i)
            if you fail to include the necessary age-related disclosures, or
            knowingly make available services/content targeted at
            Minors/Children, or (ii) if you collect, store, or process any
            Sensitive Personal Data, including credit card or bank details and
            any health information, then it shall be your sole responsibility to
            comply with all applicable laws relating to the privacy rights of
            children or any other applicable law governing the processing, use,
            or disclosure of health or bank related information, including the
            Children’s Online Privacy Protection Rule (COPPA) and the Health
            Insurance Portability and Accountability Act of 1996 (HIPPA). Graphy
            shall not be liable for your non-compliance of the applicable laws,
            including the Data Protection Laws and any health privacy
            regulation.
          </p>
          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base" id="J">
                J. Feedback:
              </span>
            </b>
          </p>
          <p className="mb-3 text-justify text-base">
            If any User(s) submits suggestions, ideas, comments, or questions
            containing product feedback about the Platform or any of the
            Services, either through the Platform or otherwise (
            <b>“Feedback”</b>), then such User(s) grants Graphy and its
            affiliates a worldwide, non- exclusive, royalty-free, perpetual, and
            irrevocable right to use (and full right to sublicense), reproduce,
            modify, adapt, publish, translate, create derivative works from,
            distribute, transmit, and display such Feedback in any form. Users
            shall have no intellectual property right in the Platform as a
            result of Graphy's incorporation of Feedback into the Platform.
          </p>
          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base" id="K">
                K. Graphy’s Rights:{" "}
              </span>
            </b>
          </p>
          <p className="mb-3 text-justify text-base">
            In respect of the entire Platform, Graphy reserves the following
            rights:
          </p>
          <div className="mb-3 text-justify text-base">
            <ol className="ml-6 list-outside list-decimal">
              <li className="pl-2 md:pl-4">
                Graphy reserves the right to put on-hold or reject or suspend or
                terminate your registration on the Platform for the purpose of
                complying with any legal or regulatory requirements.
              </li>
              <li className="pl-2 md:pl-4">
                Graphy reserves the right to remove you and/or any Content on
                Creator’s Page if you violate any of the provisions of the
                Agreement.
              </li>
              <li className="pl-2 md:pl-4">
                Graphy may modify, terminate, or refuse to provide Services at
                any time for any reason.
              </li>
              <li className="pl-2 md:pl-4">
                Graphy reserves the right to access your account and/or any
                Content on Creator's Page in order to respond to requests for
                technical support, to maintain the safety and security of the
                Platform, and for other legitimate business purposes, for legal
                or regulatory purposes or as necessary, at Graphy's discretion.
              </li>
              <li className="pl-2 md:pl-4">
                Graphy has no obligation to monitor any content that appears on
                the Platform or review any conduct occurring through the
                Platform, including any interactions between Users / End Users,
                however, if Graphy receives notice or becomes aware of, any
                violation of the Agreement, then, Graphy reserves the right to
                refuse you access to the Platform, terminate accounts or remove
                such violating content at any time.
              </li>
              <li className="pl-2 md:pl-4">
                In its sole discretion, Graphy has an absolute right to modify,
                change, alter, suspend, or terminate any provision of the
                Agreement at any time. Your continued use of the Platform or any
                of the Services, constitutes your acceptance of such change(s).
              </li>
            </ol>
          </div>
          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base" id="L">
                L. Claims against use of Third-Party Intellectual Property:{" "}
              </span>
            </b>
          </p>
          <p className="mb-3 text-justify text-base">
            At Graphy, we respect the intellectual property of others just as
            much ours. Graphy does not permit copyright infringing activities
            and infringement of intellectual property rights on the Platform,
            and Graphy will take all necessary actions in accordance with its
            Takedown Policy, if it has reason to believe that any content on the
            Platform including any Content on Creator’s Page, infringes any
            third-party intellectual property rights.
          </p>
          <p className="mb-3 text-justify text-base">
            Graphy does not endorse any Creator or Content on Creator’s Page, or
            any opinion, recommendation, or advice in connection therewith, and
            Graphy expressly disclaims any and all liability in connection with
            any Creator and/or its Creator Page(s). Hence, if you believe that
            your intellectual property rights or of any person you are aware of,
            have been used in a way that gives rise to concerns of infringement,
            then kindly write to us at{" "}
            <Link href="mailto:legal@graphy.com">legal@graphy.com</Link> with
            complete details as required under the Takedown Policy.
          </p>

          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base" id="M">
                M. Communications:{" "}
              </span>
            </b>
          </p>
          <div className="mb-3 text-justify text-base">
            <ol className="ml-6 list-outside list-decimal">
              <li className="pl-2 md:pl-4">
                <p className="mb-3 text-justify text-base">
                  <b>Communications on the Platform: </b> You agree and
                  acknowledge that Graphy is only a platform service provider
                  and does not and cannot be a party to, or has control in any
                  manner, over any transactions that happen on the Platform
                  between the Creators and their Page Users. Hence, the contract
                  of sale of Content/products/services through a Creator's Page
                  on the Platform is strictly a biparty contract between the
                  Creator (as the account holder) and their End-User(s).
                </p>
                <p className="mb-3 text-justify text-base">
                  Graphy does not monitor any interactions that takes place in
                  any discussion forum that a Creator may have enabled or any
                  communication between the Creator and their End- Users, but if
                  Graphy receives from any Page User a complaint of misconduct
                  against you, in any form, or if Graphy otherwise deems
                  necessary, Graphy reserves the right to take the necessary
                  actions in accordance with the legal obligations that Graphy
                  is required to comply with.
                </p>
                <p className="mb-3 text-justify text-base">
                  Graphy will not under any circumstance be liable for any
                  activity within the Creator’s Page and/or part thereof or for
                  any action of any User / Page User. Graphy is not responsible
                  for information that you choose to share on the Page or
                  otherwise on the Platform, or for the actions of Page Users.
                  You agree to comply with the restrictions and terms of use set
                  forth in the Platform Terms and in accordance with applicable
                  laws.
                </p>
              </li>
              <li className="pl-2 md:pl-4">
                <p className="mb-3 text-justify text-base">
                  <b>Other Communications:</b> When you visit the Platform, you
                  are communicating with us electronically. You may be required
                  to provide a valid email address and/or phone number while
                  creating any account with us or while enrolling or purchasing
                  any Subscription. We may communicate with you by e-mail, SMS,
                  phone call or by posting notices on the Platform or by any
                  other mode of communication. By providing your information you
                  authorize Graphy, its affiliates, and their respective
                  employees, agents, and contractors to initiate electronic
                  communications by email, telephone calls, or such other mode
                  of communication with respect to your use of the Platform and
                  regarding the products and Services offered by Graphy and its
                  affiliates. These communications may be made by or on behalf
                  of Graphy, even if your phone number is registered on any
                  state or federal do not call list. Telephone calls may be
                  recorded. You acknowledge that your telephone operator and/or
                  internet service provider may levy certain charges on you
                  vis-à-vis your communications with Graphy as it constitutes
                  availing their services, and you agree to be responsible for
                  all such charges, and Graphy will not be responsible for any
                  such charges. Do not submit your information if you do not
                  consent to being contacted by telephone, text, email, or such
                  other mode of communication. Further, you will always have an
                  option to unsubscribe / opt out from any promotional
                  communications sent by Graphy and / or its affiliates either
                  by following the relevant instructions that may be
                  communicated via text, email or such other mode of
                  communication in which you receive any promotional
                  communication or by disabling the options, if any, provided
                  within your account on the Platform.
                </p>
                <p className="mb-3 text-justify text-base">
                  It is hereby clarified that Graphy's communication shall only
                  be with you, and Graphy shall not directly contact your Page
                  Users in any manner (unless a Page User directly reaches out
                  to Graphy). The information referred to in this section only
                  refers to the information that you will have directly provided
                  Graphy at the time of account registration or onboarding on
                  the Platform, and specifically excludes End-User information
                  collected by a Creator through the Creator's Page.
                </p>
              </li>
            </ol>
            <p className="mb-3 text-justify text-base">Please note that –</p>
            <ol
              className="ml-6 list-outside"
              style={{ listStyle: "lower-alpha" }}
            >
              <li className="pl-2 md:pl-4">
                If you opt out of promotional communications, we may still send
                you transactional communications, such as service announcements,
                administrative and legal notices, and information about your
                account, without offering you the opportunity to opt out of
                these communications. If you no longer wish to use our Platform
                or receive any communications from us (except for those that are
                legally required), then you may delete your account by using the
                option enabled within the Platform; and
              </li>
              <li className="pl-2 md:pl-4">
                opting out of promotional communications only affects future
                communications from us. If we have already provided your
                information to a third party (as stated in our Privacy Policy)
                before you changed your preferences or updated your information,
                you may have to change your preferences directly with that third
                party.
              </li>
            </ol>
          </div>
          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base" id="N">
                N. Processing of Personal Data:
              </span>
            </b>
          </p>
          <p className="mb-3 text-justify text-base">
            All our collection, processing, sharing, and storing of any Personal
            Data (as defined in the Privacy Policy) collected from you shall be
            in accordance with our Privacy Policy. Kindly read the same to
            understand the security measures undertaken by Graphy to safeguard
            your Personal Data. Should you have any queries in respect of the
            same, please feel free to right to us at{" "}
            <Link href="mailto:care@graphy.com">care@graphy.com.</Link>
          </p>
          <p className="mb-3 text-justify text-base">
            Further, we have published the Data Processing Addendum (“DPA”) in
            accordance with the applicable Data Protection Laws, including the
            GDPR and CCPA. DPA is a document that determines our processing
            activities in respect of any information, including Personal Data,
            published on your Page and/or collected directly by you from your
            End-Users through your Page. The DPA forms an integral part of the
            Platform Terms and our Agreement; it details Creators' and Graphy's
            obligations with respect to the processing of Personal Data and
            contains your documented instructions to us in accordance with which
            we shall conduct the processing activities of the information
            referred to in this Section.
          </p>
          <p className="mb-3 text-justify text-base">
            By using our Service, you hereby agree to comply with all applicable
            Data Protection Laws and other security and regulatory compliances
            in respect of any information that you make available on the
            Platform or otherwise collect from your End-Users. You are
            responsible for protecting all Personal Data that you receive from
            or are provided with in connection with your Page, use of the
            Platform, or any of the Services. You are also responsible for
            providing an appropriate privacy notice and cookies policy to your
            End-Users that detail, among other things, the processing activities
            and security measures undertaken by you in respect of your
            End-Users’ Data (as defined in the Privacy Policy) within and beyond
            the scope of this Platform in accordance with applicable laws.
          </p>

          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base" id="O">
                O. Platform Availability:{" "}
              </span>
            </b>
          </p>
          <p className="mb-3 text-justify text-base">
            Your access to the Platform may occasionally be suspended or
            restricted to allow for repairs, maintenance, or due to the
            introduction of new facilities or services at any time without prior
            notice. We will attempt to limit the frequency and duration of any
            such suspension or restriction. You agree that Graphy will not be
            liable for any losses that may be incurred by you if for any reason
            all or part of the Platform is unavailable at any time or for any
            period for use.
          </p>

          <div className="my-3 text-justify">
            <b>
              <span className="text-base" id="P">
                P. Pricing, Payments, and Refunds:{" "}
              </span>
            </b>
          </div>

          <div className="mb-3 text-justify text-base">
            <ol className="ml-6 list-outside list-decimal">
              <li className="pl-2 md:pl-4">
                <p className="mb-3 text-justify text-base">
                  <b>Platform Pricing and Payment: </b>Graphy may charge fees
                  from Creators to access particular features and
                  functionalities of the Platform{" "}
                  <b>(“Graphy's Fee”/ “Fee(s)”)</b>
                  through multi-tiered paid plans as described on our Pricing
                  Page <b>(“Subscription”/“Subscription Plan”)</b>. Please read
                  the below terms applicable for the purchase of your
                  Subscription:
                </p>
                <div className="mb-3 text-justify text-base">
                  <ol
                    type="a"
                    className="ml-4 list-outside"
                    style={{ listStyle: "lower-alpha" }}
                  >
                    <li className="pl-1 md:pl-2">
                      We offer various types of Subscription Plans, and Graphy’s
                      Fee structure for each Subscription Plan differs. Please
                      go through the details provided on our Pricing Page
                      carefully before making any purchase.
                    </li>
                    <li className="pl-1 md:pl-2">
                      Our billing cycles depend on the Subscription Plan you
                      select while purchasing. Payment will be charged to your
                      chosen payment method at confirmation of your purchase in
                      accordance with the billing cycle applicable for your
                      Subscription Plan.
                    </li>
                    <li className="pl-1 md:pl-2">
                      We may use third-party service provider(s) to manage all
                      our Subscriptions, including their purchases, renewals,
                      cancellations, and invoicing{" "}
                      <b>(“Subscription Manager(s)”)</b>. Accordingly, we may
                      not collect any payment related information such as
                      credit/debit card details, UPIs, and bank account details
                      from you, and all such Sensitive Personal Data, in such
                      cases, shall be collected and processed by such
                      Subscription Manager(s). Subject to your authorization and
                      applicable laws, You agree that Graphy may, if applicable,
                      as per your chosen Subscription Plan, charge any recurring
                      service to the credit card or debit card that you provide
                      at the time of your first purchase of the Subscription
                      through the Subscription Manager(s) or as updated by you
                      through the Subscription Manager(s), provided such
                      updation takes place prior to upcoming billing period, as
                      applicable.
                    </li>
                    <li className="pl-1 md:pl-2">
                      You agree to pay all fees and charges that are made to
                      your account and that you are solely responsible for
                      payment of these fees and charges. If you have not
                      completed payments for your Subscriptions, we may restrict
                      your access to the Platform until your account becomes
                      current and paid in full.
                    </li>
                    <li className="pl-1 md:pl-2">
                      Your payments to Graphy shall be subject to applicable
                      taxes including without limitation Goods and Service Taxes
                      (GST) and Value Added Taxes (VAT) or other similar taxes
                      as may be applicable in your country of residence/from
                      where you have created your account on the Platform/
                      purchased the underlying Subscriptions.
                    </li>
                    <li className="pl-1 md:pl-2">
                      We reserve the right to pursue fees owed to us using
                      collection methods which may include charging other
                      payment methods on file with us and/or retaining
                      collection agencies or legal counsel.
                    </li>
                    <li className="pl-1 md:pl-2">
                      If you are unclear on the payments applicable to your
                      Subscription Plan, then please write to us at the email
                      address provided in the{" "}
                      <b>'Contact for User Support/Queries'</b> section below.
                    </li>
                    <li className="pl-1 md:pl-2">
                      We reserve the right to change/revise the pricing of the
                      Subscriptions. For existing Subscriptions for which the
                      applicable fees have been already received by us, we will
                      implement the price changes during the next billing period
                      or renewal of the Subscription.
                    </li>
                    <li className="pl-1 md:pl-2">
                      We further reserve the right to offer custom plans and
                      pricing in addition to what is offered on the Pricing
                      Page, which include offering custom billing and payment
                      terms, that are different from our standard terms.
                    </li>
                    <li className="pl-1 md:pl-2">
                      <p className="mb-3 text-justify text-base">
                        We have also enabled integration of third-party payment
                        providers to facilitate better payment options to you,
                        which may vary depending on your territory or the
                        Subscription you choose{" "}
                        <b>(“Third-Party Payment Providers”).</b>
                      </p>
                      <p className="mb-3 text-justify text-base">
                        Third-Party Payment Providers may also charge you fees
                        to use or access their services and may require your
                        Personal Data to complete any transaction for the
                        Platform. Further, to facilitate completion of your
                        payments to us through the Platform or avail the payment
                        options provided to you, you may be redirected to an
                        external website operated by the Third-Party Payment
                        Provider(s).
                      </p>
                      <p className="mb-3 text-justify text-base">
                        Further, pursuant to the payment option you may choose,
                        you may be required to enter into a separate agreement
                        with the relevant Third-Party Payment Provider. This
                        agreement with the Third-Party Payment Provider is an
                        independent contract/agreement between you and such
                        Third-Party Payment Provider and Graphy shall in no
                        manner be a party to the same. Graphy only facilitates
                        various payment options to you and is not offering the
                        payment by itself in any manner.
                      </p>
                      <p className="mb-3 text-justify text-base">
                        You agree that you are solely responsible for all
                        charges that occur through such Third-Party Payment
                        Providers and acknowledge and agree to indemnify,
                        defend, and hold harmless Graphy, its licensors, their
                        affiliates, and their respective officers, directors,
                        employees, and agents from any loss arising out of or
                        related to the use of the Platform or any purchases made
                        through the Platform. This obligation will survive your
                        use of the Platform and termination of your Agreement
                        with us.
                      </p>
                    </li>
                    <li className="pl-1 md:pl-2">
                      <p className="mb-3 text-justify text-base">
                        Graphy cannot and does not (i) guarantee the adequacy of
                        the privacy and security practices employed by or the
                        content and media provided by the Third-Party Service
                        Providers or its respective websites or (ii) control
                        collection or use of your Personal Data by such
                        Third-Party Service Providers. Hence, prior to using any
                        services offered by a Third-Party Service Providers, we
                        suggest that you read their terms and conditions,
                        privacy policy and other policies, that may apply, to
                        understand their terms of usage and to understand how
                        your Personal Data is being processed. Graphy is not
                        affiliated to any Third-Party Service Provider and
                        neither Graphy nor any of the Third-Party Service
                        Providers are agents or employees of the other.
                      </p>
                      <p className="mb-3 text-justify text-base">
                        For the purpose of the Platform Terms-
                      </p>
                      <p className="mb-3 text-justify text-base">
                        <b>“Third-Party Service Providers”</b> shall mean and
                        include Subscription Manager(s), Third-Party Payment
                        Providers, and any third-party payment gateways and/or
                        aggregators used to collect payments of Graphy's Fee or
                        used by the Creators to collect their Creator Fee(s) or
                        such other payments from their End- Users.
                      </p>
                      <p className="mb-3 text-justify text-base">
                        <b>“Loss”</b> means all losses, liabilities, damages,
                        awards, settlements, claims, suits, proceedings, costs,
                        and expenses (including reasonable legal fees and
                        disbursements and costs of investigation, litigation,
                        settlement, judgment, interest, and penalties). Graphy
                        shall not be liable to you for any claims arising out of
                        any act or omission on the part of the Third-Party
                        Service Provider(s) including, but not limited to, any
                        lost, stolen, or incorrectly processed payments. Graphy
                        expressly disclaims any responsibility and liability for
                        all services provided by the Third-Party Service
                        Provider(s).
                      </p>
                    </li>
                    <li className="pl-1 md:pl-2">
                      Please note that all Fee(s) payments are collected by
                      Graphy only through the Platform and not through any third
                      parties (except Third-Party Service Provider(s)). We do
                      not usually authorize any third party (except Third-Party
                      Service Provider(s)) to collect monies on our behalf;
                      however, if we have authorized any third party then such
                      third party will have received a written authorization
                      from Graphy either by way of any agreement or an
                      authorization letter. Kindly verify with such third party
                      before you make any payments to them, alternatively, you
                      can always check with us by writing to us at email address
                      provided under the{" "}
                      <u>
                        <b>'Contact for User Support/Queries'</b>
                      </u>
                      section below.
                    </li>
                    <li className="pl-1 md:pl-2">
                      Further, Graphy is solely authorized to offer discounts /
                      offers, if any, on Graphy's Fee. These discounts / offers
                      are communicated on the Platform or via direct
                      communication to you from Graphy via email, SMS, phone, or
                      such other means of communication, and can be availed only
                      through the Platform, unless otherwise specifically
                      communicated by Graphy. Other than Graphy, no person is
                      allowed to offer any discounts on Graphy's Fee offered on
                      the Platform. Graphy shall not be liable for any claims
                      arising from such unauthorized discounts / offers offered
                      by any person (including any third-party platform), other
                      than Graphy.
                    </li>
                  </ol>
                </div>
              </li>
              <li className="pl-2 md:pl-4">
                <p className="mb-3 text-justify text-base">
                  <b>Cancellation and Refund Policy: </b>
                  You may cancel your Subscription by either writing to us at{" "}
                  <Link href="mailto:care@graphy.com">
                    care@graphy.com
                  </Link>{" "}
                  or through your account on the Platform. However, please note
                  there shall be no refund of the Fee already paid for your
                  Subscription, unless otherwise specified in the{" "}
                  <Link href="refund-policy">Refund Policy</Link>. So, please
                  read these Terms of Service and the Refund Policy carefully
                  before subscribing to any of the Subscriptions.
                </p>
                <p className="mb-3 text-justify text-base">
                  When you cancel your Subscription, Graphy may disable access
                  to features available only to Creators who have
                  purchased/renewed Subscription, while your account may
                  continue to exist on the Platform.
                </p>
              </li>
              <li className="pl-2 md:pl-4">
                <p className="mb-3 text-justify text-base">
                  <b>Payments w.r.t Creator's Fee: </b>The Creator's Fee for the
                  Content/services made available on the Creators' Page is
                  solely determined by the respective Creators, and Graphy has
                  no role in determining the same. The Creator must make
                  available to their End-Users the terms and conditions in
                  relation to the enrolment, use of Content and related services
                  that may be provided by the Creator on their Page(s).
                </p>
                <p className="mb-3 text-justify text-base">
                  With an intent to have a more user-friendly transaction model
                  for payment of the Creator's Fee, we allow Creators to
                  integrate various third-party payment aggregators to process
                  payments for products, services, or other content purchased
                  through the Creator's Page{" "}
                  <b>(“Third-Party Payment Aggregators”)</b>
                  by End Users. Third-Party Payment Aggregators made available
                  to you may vary depending on your territory. Once selected,
                  the Third-Party Payment Aggregator(s) may need you to create
                  an account on their platform or provide the information of
                  your existing account(s) with them and agree to such
                  Third-Party Payment Aggregator's terms and conditions and
                  privacy policy. Once you have enabled payments on the Platform
                  and registered with the Third- Party Payment Aggregator(s) as
                  stated above, you will see that they are responsible for
                  directing and distributing payments for services purchased
                  through your Page, including automatically sending receipts to
                  your End-Users and directing payouts to you and any bank
                  accounts identified by you. The Third-Party Payment
                  Aggregator(s) will collect the amounts paid by your End-Users
                  and remit the collected amounts to you after deduction of
                  their fee, if any, and applicable taxes. Please note
                  Third-Party Payment Aggregators may also charge you fees to
                  use or access their services and may require your personal
                  data to complete any transaction for the Platform. Hence,
                  prior to using any services offered by a Third-Party Payment
                  Aggregators, we suggest that you read their terms and
                  conditions, privacy policy and other policies, that may apply,
                  to understand their terms of usage and to understand how your
                  data is being processed; this is also because, when you use
                  the services or platform offered by such Third-Party Payment
                  Aggregator, you agree to their respective terms and policies.
                  Graphy is not affiliated with their Third- Party Payment
                  Aggregators and neither Graphy nor any of the Third-Party
                  Payment Aggregators are agents or employees of the other.
                </p>
                <p className="mb-3 text-justify text-base">
                  Further, if you have purchased a Subscription Plan that
                  requires you to share your revenue with Graphy, then your
                  Creator's Fee may be paid to you by the Third-Party Payment
                  Aggregators after deducting Graphy's share, as agreed and
                  applicable. Please read the{" "}
                  <Link href="/pricing">Pricing Page</Link> and other terms, if
                  any, specified on the Platform in this regard.
                </p>
                <p className="mb-3 text-justify text-base">
                  <b>Please note:</b> You may not bypass or circumvent the
                  payment mechanism offered by the Platform by soliciting
                  payment from an End-User outside the Platform in any manner
                  including by using any alternative method to collect the
                  Creator Fee and/or fee/monies for any service offered by the
                  Creator on the Platform such as receiving payments through
                  links to digital wallet platforms like Google Pay, Paypal or
                  such other online payment system or electronic wallets.
                </p>
                <p className="mb-3 text-justify text-base">
                  Further, please note the below terms:
                </p>

                <div className="mb-3 text-justify text-base">
                  <ol
                    type="a"
                    className="ml-4 list-outside"
                    style={{ listStyle: "lower-alpha" }}
                  >
                    <li className="pl-1 md:pl-2">
                      In respect of your transaction, you are solely responsible
                      for all applicable Goods and Services Taxes <b>('GST')</b>
                      , EU Value Added Taxes <b>('VAT')</b>, or other similar
                      taxes as may be applicable in your country of residence,
                      costs, filings, and any other activities that may arise as
                      a result of sales of your Content / services using the
                      Platform. Neither is Graphy responsible to nor does it
                      collect, pay, remit or otherwise pay on your behalf, the
                      taxes owed by you (including GST or EU VAT) on the
                      services you sell through your Page.
                    </li>
                    <li className="pl-1 md:pl-2">
                      You set the Creator's Fee of each service offered by you
                      through your Page at your sole discretion and without any
                      input from Graphy.
                    </li>
                    <li className="pl-1 md:pl-2">
                      All invoices made to your End-Users through the respective
                      Third-Party Payment Aggregator shall indicate that you are
                      the supplier of the services offered by you on through
                      your Page.
                    </li>
                    <li className="pl-1 md:pl-2">
                      End-Users' invoices shall identify the name and nature of
                      the services offered by you that was purchased.
                    </li>
                    <li className="pl-1 md:pl-2">
                      As stated, payments in respect of your services offered by
                      you through your Page are processed by Third-Party Payment
                      Aggregators, hence, neither is Graphy a party to the
                      authorization process nor is Graphy responsible for
                      authorizing credit card charges through such Third-Party
                      Payment Aggregators.
                    </li>
                    <li className="pl-1 md:pl-2">
                      You are responsible for making available the terms and
                      conditions, privacy policy and refund policy applicable
                      for your Page. It is further agreed by you that you shall
                      indemnify, defend, and hold harmless Graphy and its
                      directors, officers, agents, employees, successors,
                      assignees, agents and authorized representatives against
                      any loss of credit, tax, interest and/or penalty resulting
                      from your failure to refund Creator's Fee in accordance
                      with your policies, remit the applicable taxes to the
                      respective authority within the stipulated time period, or
                      your failure to otherwise conform to applicable laws.
                    </li>
                    <li className="pl-1 md:pl-2">
                      Should there be any issues with payments, Graphy has no
                      responsibility to accept or reject any services offered by
                      you through your Page. You agree that once payments are
                      processed through a Third-Party Payment Aggregators, such
                      processing serves as an authorization to Graphy to deliver
                      the services to your End-Users through the Services.
                    </li>
                    <li className="pl-1 md:pl-2">
                      You hereby agree and accept that in respect of any
                      transaction, applicable taxes (direct and/or indirect)
                      shall be deducted from the payment. You shall solely be
                      liable for all your taxes (including service and
                      withholding taxes) within and/or outside India. It is
                      further agreed by you that you shall indemnify, defend,
                      and hold harmless Graphy and our directors, officers,
                      agents, employees, successors, assignees, and authorized
                      representatives against any loss of credit, tax, interest
                      and/or penalty resulting from your failure to remit the
                      applicable taxes to the respective authority within the
                      stipulated time period, or your failure to otherwise
                      conform to the applicable laws.
                    </li>
                  </ol>
                </div>
              </li>
            </ol>
          </div>

          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base" id="Q">
                Q. Customized Subscription & Service Order:
              </span>
            </b>
          </p>
          <p className="mb-3 text-justify text-base">
            To provide Creators with customized solutions and access to certain
            services and features of our Platform, Graphy may offer customized
            Subscriptions to Creators who make such a request, and Graphy's Fee
            applicable for such Subscriptions shall be dependent on the Services
            or features offered thereunder. To create and access an account
            under a customized Subscription, you must execute our service order
            <b>(“Service Order”)</b> and pay the applicable Graphy's Fee in
            accordance with the terms of such Service Order. In the event of any
            conflict or inconsistency of the terms between the Service Order and
            any other provisions of these Platform Terms, then only to the
            extent of such conflict or inconsistency, the terms of the Service
            Order shall prevail.
          </p>
          <p className="mb-3 text-justify text-base">
            Subject to execution of the Service Order, we will: (a) make the
            Services available to you in accordance with the applicable Service
            Order, and (b) host, serve and support the Services in accordance
            with our service levels, if any, agreed in the respective Service
            Order.
          </p>
          <p className="mb-3 text-justify text-base">
            We reserve the right to modify or change the Services offered to you
            at any time, provided that the modification or change (a) will be
            duly notified to you, (a) will not result in a material degradation
            or material loss of functionality of the Services; or (b) will not
            change the service levels provided in the Service Order except as
            permitted by this section.
          </p>

          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base" id="R">
                R. Deletion of Account:{" "}
              </span>
            </b>
          </p>

          <p className="mb-3 text-justify text-base">
            You may delete your account at any time by using the delete option
            provided within your Page. If your account is deleted (regardless of
            the reason), all Content on your Page may no longer be available and
            the same may be irrecoverable. Graphy is not responsible for the
            loss of such Content on Creator's Page upon deletion and Graphy
            shall not be liable to any party in any way for their inability to
            access any such Content on Creator's Page arising from any deletion.
            Please note that accounts without paid Subscriptions may be deleted
            from the Platform if they remain inactive (i.e., the user fails to
            log in) for a continuous period of at least six (6) months and
            accounts on paid Subscription will remain active unless you
            explicitly ask us to delete it when you cancel your paid plan.
            Further, the account may be deleted in accordance with other terms
            set out in these Platform Terms.
          </p>
          <p className="mb-3 text-justify text-base">
            Please note that we may need to retain some or all of the
            information that we have collected from you directly (i.e.,
            excluding any Content on Creator's Page and Creator's End-Users'
            Data) in order comply with our legal and business obligations.
            Please refer to our Privacy Policy to know our data retention
            practices.
          </p>

          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base" id="S">
                S. Disclaimer:{" "}
              </span>
            </b>
          </p>
          <p className="mb-3 text-justify text-base">
            YOU AGREE THAT THE PLATFORM, CONTENT, AND ALL MATERIALS ARE PROVIDED
            ON AN “AS IS” AND “AS AVAILABLE” BASIS AND YOUR USE OF THE PLATFORM
            SHALL BE AT YOUR SOLE RISK. TO THE FULLEST EXTENT PERMITTED BY LAW,
            GRAPHY, ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS DISCLAIM AND
            EXCLUDE ALL WARRANTIES, EXPRESS OR IMPLIED, IN CONNECTION WITH THE
            PLATFORM AND YOUR USE THEREOF. TO THE FULLEST EXTENT PERMITTED BY
            LAW, GRAPHY EXCLUDES ALL WARRANTIES, CONDITIONS, TERMS OR
            REPRESENTATIONS ABOUT THE ACCURACY, SECURITY, RELIABILITY, QUALITY,
            AVAILABILITY OR COMPLETENESS OF THE PLATFORM, CONTENT ON CREATOR'S
            PAGE, OR THE CONTENT OF ANY SITES SO LINKED AND ASSUMES NO LIABILITY
            OR RESPONSIBILITY FOR ANY (I) ERRORS, MISTAKES, OR INACCURACIES OF
            ANY CONTENT ON CREATOR'S PAGE OR ANY CONTENT ON THE PLATFORM, (II)
            PERSONAL INJURY OR PROPERTY DAMAGE, OF ANY NATURE WHATSOEVER,
            RESULTING FROM YOUR ACCESS TO AND USE OF THE PLATFORM, (III) ANY
            UNAUTHORIZED ACCESS TO OR USE OF OUR SECURE SERVERS AND/OR ANY AND
            ALL PERSONAL DATA AND/OR FINANCIAL INFORMATION STORED THEREIN, (IV)
            ANY INTERRUPTION OR CESSATION OF TRANSMISSION TO OR FROM THE
            PLATFORM, (IV) ANY BUGS, VIRUSES, TROJAN HORSES, OR THE LIKE WHICH
            MAY BE TRANSMITTED TO OR THROUGH THE PLATFORM BY ANY THIRD PARTY,
            AND/OR (V) ANY ERRORS OR OMISSIONS IN ANY CONTENT ON CREATOR'S PAGE
            OR ANY OTHER CONTENT OR FOR ANY LOSS OR DAMAGE OF ANY KIND INCURRED
            AS A RESULT OF THE USE OF ANY CONTENT ON CREATOR'S PAGE OR ANY OTHER
            CONTENT THAT IS POSTED, EMAILED, TRANSMITTED, OR OTHERWISE MADE
            AVAILABLE VIA THE PLATFORM AS GRAPHY IS STRICTLY AN INTERMEDIARY AS
            UNDER THE INFORMATION TECHNOLOGY ACT, 2000 AND SUCH OTHER SIMILAR
            APPLICABLE LAWS. GRAPHY DOES NOT WARRANT, ENDORSE, GUARANTEE, OR
            ASSUME RESPONSIBILITY FOR ANY CONTENT ON CREATOR'S PAGE OR THE
            PLATFORM ADVERTISED OR OFFERED BY A THIRD PARTY THROUGH THE PLATFORM
            OR ANY HYPERLINKED SERVICES OR FEATURED IN ANY BANNER OR OTHER
            ADVERTISING, AND GRAPHY WILL NOT BE A PARTY TO OR IN ANY WAY BE
            RESPONSIBLE FOR MONITORING ANY TRANSACTION BETWEEN YOU AND
            THIRD-PARTY PROVIDERS OF CONTENT OR SERVICES ON THE CREATOR'S PAGE.
            AS WITH THE PURCHASE OF A SERVICE THROUGH ANY MEDIUM OR IN ANY
            ENVIRONMENT, YOU SHOULD USE YOUR BEST JUDGMENT AND EXERCISE CAUTION
            WHERE APPROPRIATE.
          </p>

          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base" id="T">
                T. Limitation of Liability:{" "}
              </span>
            </b>
          </p>
          <p className="mb-3 text-justify text-base">
            TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT SHALL, GRAPHY,
            ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS, BE LIABLE TO YOU FOR
            ANY DIRECT, INDIRECT, INCIDENTAL, EXEMPLARY, SPECIAL, PUNITIVE,
            LOSSES OR EXPENSES OR CONSEQUENTIAL DAMAGES WHATSOEVER ARISING OUT
            OF, RESULTING FROM, OR IN CONNECTION WITH YOUR USE, OR INABILITY TO
            USE, THE PLATFORM, ANY CONTENT ON THE PLATFORM, OR SUCH OTHER
            SERVICES OR ITEMS OBTAINED THROUGH THE PLATFORM INCLUDING, BUT NOT
            LIMITED TO ANY (I) ERRORS, MISTAKES, OR INACCURACIES OF ANY CONTENT
            ON CREATOR'S PAGE OR ANY OTHER CONTENT AVAILABLE ON THE PLATFORM,
            (II) PERSONAL INJURY OR PROPERTY DAMAGE, OF ANY NATURE WHATSOEVER,
            RESULTING FROM YOUR ACCESS TO AND USE OF OUR PLATFORM, (III) ANY
            UNAUTHORIZED ACCESS TO OR USE OF OUR SECURE SERVERS AND/OR ANY AND
            ALL PERSONAL DATA AND/OR FINANCIAL INFORMATION STORED THEREIN, (IV)
            ANY INTERRUPTION OR CESSATION OF TRANSMISSION TO OR FROM THE
            PLATFORM, (IV) ANY BUGS, VIRUSES, TROJAN HORSES, OR THE LIKE, WHICH
            MAY BE TRANSMITTED TO OR THROUGH OUR PLATFORM BY ANY THIRD PARTY,
            AND/OR (V) ANY ERRORS OR OMISSIONS IN ANY CONTENT ON CREATOR'S PAGE
            OR ANY OTHER CONTENT OR FOR ANY LOSS OR DAMAGE OF ANY KIND INCURRED
            AS A RESULT OF YOUR USE OF ANY CONTENT POSTED, EMAILED, TRANSMITTED,
            OR OTHERWISE MADE AVAILABLE VIA THE PLATFORM, WHETHER BASED ON
            WARRANTY, CONTRACT, TORT, OR ANY OTHER LEGAL THEORY, AND WHETHER OR
            NOT GRAPHY IS ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </p>
          <p className="mb-3 text-justify text-base">
            YOU SPECIFICALLY ACKNOWLEDGE THAT GRAPHY SHALL NOT BE LIABLE FOR ANY
            CONTENT ON CREATOR'S PAGE OR THE DEFAMATORY, OFFENSIVE, OR ILLEGAL
            CONDUCT OF ANY THIRD PARTY AND THAT THE RISK OF HARM OR DAMAGE FROM
            THE FOREGOING RESTS ENTIRELY WITH YOU.
          </p>
          <p className="mb-3 text-justify text-base">
            THE PLATFORM IS CONTROLLED AND OFFERED BY GRAPHY AND / OR ITS
            AFFILIATES DEPENDING UPON YOUR JURISDICTION. GRAPHY MAKES NO
            REPRESENTATIONS THAT THE PLATFORM IS APPROPRIATE OR AVAILABLE FOR
            USE IN OTHER LOCATIONS. THOSE WHO ACCESS OR USE THE PLATFORM FROM
            OTHER JURISDICTIONS DO SO AT THEIR OWN VOLITION AND ARE RESPONSIBLE
            FOR COMPLIANCE WITH LOCAL LAW.
          </p>

          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base" id="U">
                U. Indemnity and Release:{" "}
              </span>
            </b>
          </p>
          <p className="mb-3 text-justify text-base">
            You agree to indemnify and hold harmless Graphy and its successors
            and assigns and all of their respective officers, directors,
            subsidiaries, affiliates, agents and employees, from any claims,
            liability, judgments, awards, losses, obligations, costs, expenses
            or fees (including reasonable attorneys' fees), demands, or actions
            made by any third party or penalty imposed due to or arising out of
            your breach of the Agreement or any document incorporated by
            reference, any claim that content provided by you caused damage or
            loss to a third party, or your violation of any law, rules,
            regulations or the rights of a third
          </p>
          <p className="mb-3 text-justify text-base">
            You hereby expressly release Graphy and any of its officers and
            representatives from any cost, damage, liability, or other
            consequence of any actions/inactions of any third-party vendors or
            service providers and specifically waive any claims or demands that
            you may have in this behalf against Graphy under any statute,
            contract or otherwise.
          </p>
          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base">V. OFAC:</span>
            </b>
          </p>
          <p className="mb-3 text-justify text-base">
            You represent and warrant that you are not, nor are you owned or
            controlled directly or indirectly by, any person, group, entity or
            nation named on any list issued by the Department of the Treasury's
            Office of Foreign Asset Control <b>(“OFAC” )</b>, or any similar
            list or by any law, order, rule or regulation or any Executive Order
            of the President of the United States, including Executive Order
            13224, as a 'Specially Designated National and Blocked Person',
            terrorist, or other banned or blocked person (any such person,
            group, entity or nation being hereinafter referred to as a{" "}
            <b>(“Prohibited Person”)</b>; (ii) you are not (nor are you owned or
            controlled, directly or indirectly, by any person, group, entity or
            nation which is) acting directly or indirectly for or on behalf of
            any Prohibited Person; (iii) you (and any person, group, or entity
            which you control, directly or indirectly) have not conducted nor
            will conduct business nor have engaged nor will engage in any
            transaction or dealing with any Prohibited Person in violation of
            the U.S. Patriot Act or any OFAC rule or regulation, including,
            without limitation, the making or receiving of any contribution of
            funds, good or services to or for the benefit of a Prohibited Person
            in violation of the U.S. Patriot Act or any OFAC rule or regulation;
            (iv) you are not prohibited by any sanctions program as maintained
            by OFAC from transacting with Graphy, including those sanctions
            programs currently in effect with respect to Cuba, Iran, North
            Korea, Syria, Venezuela, or Crimea. In connection with the
            foregoing, it is expressly understood and agreed that the
            representations and warranties contained in this subsection shall be
            continuing in nature.
          </p>

          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base" id="W">
                W. General Provisions:{" "}
              </span>
            </b>
          </p>
          <div className="mb-3 text-justify text-base">
            <ol className="ml-6 list-outside list-decimal">
              <li className="pl-2 md:pl-4">
                <b>Additional Representation:</b> You hereby confirm and
                represent that you have the requisite capacity, power, and
                authority to enter into this Agreement with us and that you have
                not entered into any agreement or arrangement with any
                third-party (including without limitation with{" "}
                {COMPANY_LEGAL_NAME} (which is the parent company of Graphy) or
                Graphy India or Graphy Inc for any of its other brands, products
                or services, its subsidiaries, and affiliates) which would
                prevent you from entering into this Agreement with us. If you
                have any such agreements/arrangements, then you understand that
                you will solely be responsible to obtain necessary permissions,
                waivers, etc., as may be required, from such third-party for
                using our Platform. Any violation/objection raised by or any
                claim from such third-party shall solely be your liability, and
                you shall indemnify us against any claims, damages or liability
                under this clause.
              </li>
              <li className="pl-2 md:pl-4">
                <b>Modification, Amendment or Termination: </b>Graphy may, in
                its sole discretion, modify or revise the Agreement and policies
                at any time, and you agree to be bound by such modifications or
                revisions. Your continued use of the Platform post any
                modification of the Agreement shall be taken as your consent and
                acceptance to such modifications. Nothing in the Agreement shall
                be deemed to confer any third-party rights or benefits. You are
                advised to check our Platform frequently to see recent changes
                and to keep yourself updated with the most recent updates.
              </li>
              <li className="pl-2 md:pl-4">
                <b>Force Majeure: </b>Graphy shall not be liable for failure to
                perform, or the delay in performance of, any of its obligations
                if, and to the extent that, such failure or delay is caused by
                events substantially beyond its control, including but not
                limited to acts of God, acts of the public enemy or governmental
                body in its sovereign or contractual capacity, war, terrorism,
                floods, fire, strikes, epidemics, civil unrest or riots, power
                outage, and/or unusually severe weather.
              </li>
              <li className="pl-2 md:pl-4">
                <b>Miscellaneous:</b> If any part of the Agreement is found to
                be unlawful, void or unenforceable, that part of the Agreement
                will be deemed severable and will not affect the validity and
                enforceability of any remaining provisions. Any notice required
                to be given in connection with the Platform shall be in writing
                and sent to the registered office of Graphy. We do not guarantee
                continuous, uninterrupted, or secure access to the Platform, and
                operation of the Platform may be interfered by numerous factors
                outside our control. Headings are for reference purpose only and
                in no way define, limit, construe or describe the scope or
                extent of such section. Our failure to act with respect to any
                breach by you or others does not waive our right to act with
                respect to subsequent or similar breaches.
              </li>
            </ol>
          </div>

          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base" id="X">
                X. Contact for User Support/Queries
              </span>
            </b>
          </p>

          <p className="mb-3 text-justify text-base">
            For queries relating to Services offered by Graphy, please write to
            us at <Link href="mailto:care@graphy.com">care@graphy.com</Link>.
          </p>
          <div className="my-4 pt-8 text-center">
            <span className="text-base font-bold">
              II. COUNTRY SPECIFIC TERMS AND CONDITIONS
            </span>
          </div>
          <div className="mb-3 flex justify-center">
            <u>
              <span className="text-base" id="A2">
                Applicable in respect of Content published by Creators from
                India.
              </span>
            </u>
          </div>

          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base">
                A. Governing Law, and Jurisdiction:
              </span>
            </b>
          </p>
          <p className="mb-3 text-justify text-base">
            The Agreement shall be governed by and construed in accordance with
            the laws of India; and you agree, as we do, to submit to the
            exclusive jurisdiction of the courts at Bangalore, Karnataka, India.
          </p>

          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base">B. Consumer Grievances:</span>
            </b>
          </p>

          <p className="mb-3 text-justify text-base">
            Nodal Officer Details - <br /> Name: Siddharth Manchanda (Legal
            Department)
            <br />
            Contact:{" "}
            <Link href="mailto:legal@graphy.com">legal@graphy.com</Link> <br />
            <br />
            Grievance Officer Details - <br /> Name: Gourav Kakkar
            <br /> Contact:{" "}
            <Link href="mailto:care@graphy.com">care@graphy.com</Link>
          </p>

          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base">C. Contact Us:</span>
            </b>
          </p>
          <p className="mb-3 text-justify text-base">
            If you have concerns or queries regarding the Agreement, you may
            write to us by email at{" "}
            <Link href="mailto:legal@graphy.com">legal@graphy.com</Link> or by
            post to: <br />
            <br />
            <b>{COMPANY_LEGAL_NAME}</b>
            <br />
            <br />
            <u>Registered office:</u> Maruti Infotech Centre, 3rd Floor,
            A-Block, Domlur, Koramangala Inner Ring Road, Bangalore- 560 071,
            Karnataka, India.
            <br />
            <br />
            <u>Corporate Office:</u> Park Plaza - XJJP+6R9, Indira Nagar II
            Stage, Hoysala Nagar, Indiranagar, Bengaluru, Karnataka 560038
          </p>

          <div className="mb-3 flex justify-center">
            <u>
              <span className="text-base" id="B2">
                Applicable in respect of Content published by Creators from
                outside India.
              </span>
            </u>
          </div>

          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base">
                A. California Civil Code Section 1789.3:
              </span>
            </b>
          </p>
          <p className="mb-3 text-justify text-base">
            Californian residents using the Platform are entitled to know that
            they may file grievances and complaints with California Department
            of Consumer Affairs, 400 R Street, Suite 1080, Sacramento, CA 95814;
            or by phone at 916-445-1254 or 800- 952-5210; or by email to
            <Link href="mailto:dca@dca.ca.gov">dca@dca.ca.gov</Link>.
          </p>

          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base">
                B. Governing Law, Legal Disputes and Jurisdiction:
              </span>
            </b>
          </p>

          <p className="mb-3 text-justify text-base">
            In the unlikely event of dispute between us, the Agreement shall be
            governed by and construed in accordance with the laws of State of
            Delaware, U.S.A. and you agree, as we do, to submit to the exclusive
            jurisdiction of the courts at State of Delaware, U.S.A.
          </p>

          <p className="mb-3 text-justify text-base">
            <b>
              <span className="text-base">C. Contact Us:</span>
            </b>
          </p>
          <p className="mb-3 text-justify text-base">
            If you have concerns or queries regarding the Agreement, you may
            write to us by email at{" "}
            <Link href="mailto:legal@graphy.com">legal@graphy.com</Link> or by
            post to: <br />
            <br />
            <b>Graphy INC</b> <br /> Vistra (Delaware) Ltd, 3500 South Dupont
            HWY
            <br /> Dover, Kent, DE 19901
          </p>
        </div>
      </div>
    </PageTemplate>
  );
}
